import { useAuth } from "@/contexts/AuthContext";
import { TenantChip, RoleChip } from "@/components/common/TenantChip";
import { TID } from "@/constants/testIds";
import { LogOut, Search } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";

export default function TopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase();

  return (
    <div
      data-testid={TID.topbar}
      className="h-14 border-b border-border bg-white/95 backdrop-blur px-6 flex items-center justify-between sticky top-0 z-30"
    >
      <div className="flex items-center gap-3">
        <div data-testid={TID.tenantSwitcher}>
          <TenantChip name={user?.tenant_name || "—"} color="#0A2540" />
        </div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>Consumer Intelligence Profile</span>
          <span className="text-altara-teal">·</span>
          <span>Primary Domain Object</span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-sm bg-slate-50 text-xs text-muted-foreground w-72">
          <Search className="w-3.5 h-3.5" />
          <span className="opacity-60">Search consumers, evidence, rules…</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={TID.userMenu} className="flex items-center gap-2 altara-focus-ring rounded-sm px-1">
              <Avatar className="w-7 h-7 rounded-sm">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="rounded-sm bg-altara-deep text-white text-[11px]">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-xs font-medium text-slate-800">{user?.name}</span>
                <span className="text-[10px] text-muted-foreground">{user?.email}</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Signed in
            </DropdownMenuLabel>
            <div className="px-2 py-1.5 text-xs">
              <div className="font-medium">{user?.name}</div>
              <div className="text-muted-foreground">{user?.email}</div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Roles</DropdownMenuLabel>
            <div className="px-2 py-1.5 flex flex-wrap gap-1">
              {(user?.roles || []).map(r => <RoleChip key={r} role={r} />)}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid={TID.logoutBtn}
              onClick={async () => { await logout(); nav("/login", { replace: true }); }}
              className="text-rose-700 focus:text-rose-800"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
